import React, { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
    Atom, Zap, Waves, Activity, Gauge, Thermometer, Sun, Moon, Eye, Compass,
    Battery, Signal, Radio, Power, Globe, Satellite, Magnet, Target, Crosshair, Triangle,
    FlaskConical, TestTube2, Microscope, Droplets, Flame, Dna, Filter, RefreshCw,
    Sparkles, Layers, Package, Box, Plus, Divide, Percent, Hash,
    Brain, Heart, HeartPulse, Leaf, TreePine, Bug, Fingerprint, Fish, Bird, Egg,
    Apple, Sprout, Flower2, Bone, Wind, Cloud, CloudSnow,
    Map, MapPin, Mountain, CloudRain, Flag, Building2, Navigation, Landmark,
    Crown, Shield, Clock, History, Archive, Scroll, Newspaper, FileText,
    Scale, Key, Lock, GraduationCap, Star, Bookmark,
    Languages, Type, AlignLeft, Quote, MessageSquare, Headphones, Mic, Volume2,
    MessageCircle, Send, BookOpen, Book, BookMarked,
    AlignJustify, Bold, Italic, Underline, List, ListOrdered, Pen, Pencil,
    Edit, Heading1, Heading2, Heading3, Feather,
    Calculator, Pi, BarChart2, PieChart, TrendingUp, Ruler, Minus,
    Hexagon, Circle, Square, Maximize2, Move, RotateCcw, Expand,
} from 'lucide-react';
import './DeckIconPicker.css';

export const ICON_MAP: Record<string, LucideIcon> = {
    Atom, Zap, Waves, Activity, Gauge, Thermometer, Sun, Moon, Eye, Compass,
    Battery, Signal, Radio, Power, Globe, Satellite, Magnet, Target, Crosshair, Triangle,
    FlaskConical, TestTube2, Microscope, Droplets, Flame, Dna, Filter, RefreshCw,
    Sparkles, Layers, Package, Box, Plus, Divide, Percent, Hash,
    Brain, Heart, HeartPulse, Leaf, TreePine, Bug, Fingerprint, Fish, Bird, Egg,
    Apple, Sprout, Flower2, Bone, Wind, Cloud, CloudSnow,
    Map, MapPin, Mountain, CloudRain, Flag, Building2, Navigation, Landmark,
    Crown, Shield, Clock, History, Archive, Scroll, Newspaper, FileText,
    Scale, Key, Lock, GraduationCap, Star, Bookmark,
    Languages, Type, AlignLeft, Quote, MessageSquare, Headphones, Mic, Volume2,
    MessageCircle, Send, BookOpen, Book, BookMarked,
    AlignJustify, Bold, Italic, Underline, List, ListOrdered, Pen, Pencil,
    Edit, Heading1, Heading2, Heading3, Feather,
    Calculator, Pi, BarChart2, PieChart, TrendingUp, Ruler, Minus,
    Hexagon, Circle, Square, Maximize2, Move, RotateCcw, Expand,
};

interface Subject {
    label: string;
    icons: string[];
}

const SUBJECTS: Subject[] = [
    {
        label: 'Física',
        icons: [
            'Atom', 'Zap', 'Waves', 'Activity', 'Gauge', 'Thermometer',
            'Sun', 'Moon', 'Compass', 'Battery', 'Signal', 'Radio',
            'Power', 'Globe', 'Satellite', 'Magnet', 'Target', 'Crosshair',
            'Triangle', 'Eye',
        ],
    },
    {
        label: 'Química',
        icons: [
            'FlaskConical', 'TestTube2', 'Microscope', 'Droplets', 'Flame', 'Dna',
            'Filter', 'RefreshCw', 'Sparkles', 'Layers', 'Package', 'Box',
            'Plus', 'Divide', 'Percent', 'Hash', 'Atom', 'Circle',
            'Zap', 'Eye',
        ],
    },
    {
        label: 'Biologia',
        icons: [
            'Dna', 'Brain', 'Heart', 'HeartPulse', 'Activity', 'Leaf',
            'TreePine', 'Bug', 'Fingerprint', 'Fish', 'Bird', 'Egg',
            'Apple', 'Sprout', 'Flower2', 'Bone', 'Wind', 'Cloud',
            'Eye', 'Microscope',
        ],
    },
    {
        label: 'Geografia',
        icons: [
            'Globe', 'Map', 'MapPin', 'Compass', 'Mountain', 'Cloud',
            'CloudRain', 'Sun', 'Layers', 'Landmark', 'Building2', 'Navigation',
            'Flag', 'Wind', 'Waves', 'CloudSnow', 'Flame', 'Leaf',
            'TreePine', 'Eye',
        ],
    },
    {
        label: 'História',
        icons: [
            'Landmark', 'Crown', 'Shield', 'Clock', 'History', 'Archive',
            'BookOpen', 'Scroll', 'Flag', 'Building2', 'Scale', 'Key',
            'Lock', 'Globe', 'Map', 'Newspaper', 'FileText', 'GraduationCap',
            'Star', 'Bookmark',
        ],
    },
    {
        label: 'Inglês',
        icons: [
            'Languages', 'Type', 'AlignLeft', 'Quote', 'MessageSquare', 'Headphones',
            'Mic', 'Volume2', 'MessageCircle', 'Send', 'BookOpen', 'Book',
            'BookMarked', 'Globe', 'Pen', 'Pencil', 'Edit', 'FileText',
            'Feather', 'Star',
        ],
    },
    {
        label: 'Gramática',
        icons: [
            'Type', 'AlignLeft', 'AlignJustify', 'Bold', 'Italic', 'Underline',
            'List', 'ListOrdered', 'Quote', 'FileText', 'Pen', 'Pencil',
            'Edit', 'Hash', 'Heading1', 'Heading2', 'Heading3', 'MessageSquare',
            'Scroll', 'BookOpen',
        ],
    },
    {
        label: 'Literatura',
        icons: [
            'Book', 'BookOpen', 'BookMarked', 'Scroll', 'Feather', 'Pen',
            'Quote', 'FileText', 'Newspaper', 'Archive', 'Star', 'Heart',
            'Moon', 'Sparkles', 'Crown', 'Bookmark', 'AlignLeft', 'Layers',
            'Languages', 'MessageSquare',
        ],
    },
    {
        label: 'Matemática',
        icons: [
            'Calculator', 'Plus', 'Minus', 'Divide', 'Percent', 'Pi',
            'BarChart2', 'PieChart', 'TrendingUp', 'Circle', 'Triangle', 'Square',
            'Hash', 'Ruler', 'Layers', 'Target', 'Gauge', 'Activity',
            'Move', 'Crosshair',
        ],
    },
    {
        label: 'Geometria',
        icons: [
            'Triangle', 'Circle', 'Square', 'Hexagon', 'Star', 'Ruler',
            'Compass', 'Box', 'Maximize2', 'Move', 'RotateCcw', 'Expand',
            'Layers', 'Target', 'Crosshair', 'Plus', 'Minus', 'Eye',
            'Hash', 'Percent',
        ],
    },
];

interface DeckIconPickerProps {
    value: string | null;
    onChange: (iconName: string | null) => void;
}

const DeckIconPicker: React.FC<DeckIconPickerProps> = ({ value, onChange }) => {
    const [activeSubject, setActiveSubject] = useState(0);

    const currentSubjectIcons = SUBJECTS[activeSubject].icons.filter(name => name in ICON_MAP);

    return (
        <div className="dip-root">
            <div className="dip-tabs">
                {SUBJECTS.map((s, i) => (
                    <button
                        key={s.label}
                        type="button"
                        className={`dip-tab ${i === activeSubject ? 'dip-tab--active' : ''}`}
                        onClick={() => setActiveSubject(i)}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="dip-grid">
                {currentSubjectIcons.map(name => {
                    const Icon = ICON_MAP[name];
                    const isActive = value === name;
                    return (
                        <button
                            key={name}
                            type="button"
                            className={`dip-icon-btn ${isActive ? 'dip-icon-btn--active' : ''}`}
                            onClick={() => onChange(isActive ? null : name)}
                            title={name}
                        >
                            <Icon size={18} />
                        </button>
                    );
                })}
            </div>

            {value && (
                <button
                    type="button"
                    className="dip-clear-btn"
                    onClick={() => onChange(null)}
                >
                    Remover ícone selecionado
                </button>
            )}
        </div>
    );
};

export default DeckIconPicker;
